import React, { useState, useEffect } from 'react';
import { Globe, ToggleLeft, ToggleRight, Settings, BarChart2, Users, Link } from 'lucide-react';
import { channelManagerAPI } from '../../lib/api';
import toast from 'react-hot-toast';

const OTA_LOGOS = {
  airbnb: '🏠', booking_com: '🔵', vrbo: '🌴', expedia: '✈️', tripadvisor: '🦉'
};

export default function ChannelManagerAdminPage() {
  const [configs, setConfigs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState({});

  const fetchData = async () => {
    try {
      setLoading(true);
      const [statsRes, configsRes] = await Promise.all([
        channelManagerAPI.getSystemStats(),
        channelManagerAPI.getAdminConfigs()
      ]);
      setStats(statsRes.data.data);
      setConfigs(configsRes.data.data || []);
    } catch {
      toast.error('Failed to load channel manager data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleToggle = async (ota, currentState) => {
    setToggling(p => ({ ...p, [ota]: true }));
    try {
      const res = await channelManagerAPI.toggleOTA(ota, !currentState);
      toast.success(res.data.message);
      setConfigs(prev => prev.map(c => c.ota === ota ? { ...c, isGloballyEnabled: !currentState } : c));
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Toggle failed');
    } finally {
      setToggling(p => ({ ...p, [ota]: false }));
    }
  };

  if (loading) return <div className="loading-center"><div className="spinner spinner-lg" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Channel Manager — Admin</h1>
          <p className="page-subtitle">Enable/disable OTA channels globally for all tenants</p>
        </div>
      </div>

      {/* System Stats */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
          {[
            { icon: Globe, label: 'Enabled OTAs', value: stats.enabledOTAs, color: '#3B82F6' },
            { icon: Users, label: 'Connected Accounts', value: stats.activeAccounts + ' / ' + stats.totalAccounts, color: '#10B981' },
            { icon: Link, label: 'Mapped Properties', value: stats.totalMappings, color: '#8B5CF6' },
            { icon: BarChart2, label: 'Bookings Imported', value: stats.bookingsImported, color: '#F59E0B' }
          ].map(({ icon: Icon, label, value, color }) => (
            <div key={label} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={20} color={color} />
                </div>
                <div>
                  <div style={{ fontSize: 22, fontWeight: 700 }}>{value}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>{label}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* OTA Configs */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {configs.map(config => (
          <div key={config.ota} className="card" style={{ padding: '20px 24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ fontSize: 36 }}>{OTA_LOGOS[config.ota]}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>{config.displayName}</div>
                  <div style={{ fontSize: 13, color: '#6B7280', maxWidth: 500 }}>{config.description}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
                    {config.supportedFeatures && Object.entries(config.supportedFeatures).filter(([, v]) => v).map(([k]) => (
                      <span key={k} style={{ fontSize: 11, padding: '2px 8px', background: '#F3F4F6', color: '#374151', borderRadius: 999 }}>
                        {k.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    ))}
                    <span style={{ fontSize: 11, padding: '2px 8px', background: '#F0FDF4', color: '#166534', borderRadius: 999 }}>
                      Sync every {config.syncIntervalMinutes}min
                    </span>
                    {config.webhookSupported && (
                      <span style={{ fontSize: 11, padding: '2px 8px', background: '#EFF6FF', color: '#1D4ED8', borderRadius: 999 }}>
                        Webhooks
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 4 }}>
                    Required credentials: {config.requiredCredentials?.length || 0}
                  </div>
                  <span className={`badge ${config.isGloballyEnabled ? 'badge-green' : 'badge-gray'}`}>
                    {config.isGloballyEnabled ? 'Globally Enabled' : 'Disabled'}
                  </span>
                </div>
                <button
                  onClick={() => handleToggle(config.ota, config.isGloballyEnabled)}
                  disabled={toggling[config.ota]}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
                  title={config.isGloballyEnabled ? 'Disable globally' : 'Enable globally'}
                >
                  {config.isGloballyEnabled
                    ? <ToggleRight size={40} color="#10B981" />
                    : <ToggleLeft size={40} color="#9CA3AF" />
                  }
                </button>
              </div>
            </div>

            {config.requiredCredentials?.length > 0 && (
              <div style={{ marginTop: 14, padding: '10px 14px', background: '#F9FAFB', borderRadius: 8 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Required credentials:</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {config.requiredCredentials.map(cred => (
                    <span key={cred.key} style={{ fontSize: 11, padding: '3px 10px', background: cred.isSecret ? '#FEF3C7' : '#EFF6FF', color: cred.isSecret ? '#92400E' : '#1D4ED8', borderRadius: 999 }}>
                      {cred.label} {cred.isSecret ? '🔒' : ''}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
